import { memo, useMemo } from "react";
import { useIsMinDesktopSize, useIsMinDesktopSizeL } from "~/common/hooks/useMediaQuery";
import { type ColumnDef } from "@tanstack/react-table";
import { TanStackTable } from "~/common/components/Table/TanStackTable";
import { useTranslation } from "react-i18next";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { getCountriesListFromFifteendigit } from "~/domains/product/utils/productDescriptorUtils";
import { getAliases, ProductAliases } from "~/domains/product/components/ProductAliases";
import { Accordion, AccordionSummary, Stack, Typography } from "@schaeffler-commerce/design-system";
import { PCAccordionDetails, PCTableContainer } from "./ComparisionTableStyles";
import { uniqueValues } from "~/common/utils/misc";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";

interface ProductDetailsTableProps {
  products: ProductDescriptor[];
  placeholders: number[];
  showOnlyDifference: boolean;
}
export interface RowData {
  index: number;
}
const ProductDetailsTable = memo(({ products, placeholders, showOnlyDifference }: ProductDetailsTableProps) => {
  const { t } = useTranslation();
  const isDesktopL = useIsMinDesktopSizeL();
  const isDesktop = useIsMinDesktopSize();
  const hasPnaPermission = userHasPermission(Permission.PRICE) && userHasPermission(Permission.AVAILABILITY);

  const tableProducts = products.map((product: ProductDescriptor) => {
    const SchaefflerId = product?.base?.sapMaterialNumber;
    const customerMaterailId = <ProductAliases product={product.base} />;
    const customerMaterialIdValue = uniqueValues(getAliases(product.base, "all")).join(",");
    const countryOfOrigin = getCountriesListFromFifteendigit({
      descriptor: product,
    }).join(", ");

    const replaces = product?.base?.oldDesignation;

    return {
      SchaefflerId,
      customerMaterailId,
      customerMaterialIdValue,
      countryOfOrigin,
      replaces,
    };
  });

  const hasProductDetailsData = tableProducts.some(
    (product) =>
      !!(
        product.SchaefflerId?.trim() ||
        product.customerMaterialIdValue?.trim() ||
        product.countryOfOrigin?.trim() ||
        product.replaces?.trim()
      ),
  );

  const data = useMemo(() => {
    const allRows = Array.from({ length: hasPnaPermission ? 4 : 2 }, (_, index) => ({
      index: hasPnaPermission ? index : index + 2,
    }));

    if (!showOnlyDifference) {
      return allRows;
    }

    return allRows.filter((row) => {
      const values = tableProducts.map((product) => {
        switch (row.index) {
          case 0:
            return product.SchaefflerId ?? "";

          case 1:
            return product.customerMaterialIdValue ?? "";

          case 2:
            return product.countryOfOrigin ?? "";

          case 3:
            return product.replaces ?? "";

          default:
            return "";
        }
      });

      return new Set(values.map((value) => value.toString().trim().toLowerCase())).size > 1;
    });
  }, [hasPnaPermission, tableProducts, showOnlyDifference]);

  const columnConfig: ColumnDef<RowData>[] = useMemo(() => {
    const columnConfig: ColumnDef<RowData>[] = [];
    const labelCol: ColumnDef<RowData> = {
      id: "label",
      size: 246,

      cell: (cell) => {
        const data = cell.row.original;
        switch (data.index) {
          case 0:
            return t("productDetailsTable.schaefflerId");
          case 1:
            return t("productDetailsTable.customerMaterialId");
          case 2:
            return t("productDetailsTable.coo");
          case 3:
            return t("comparisonCard.replaces");
          default:
            return null;
        }
      },
    };

    const makeProductCol = (index: number) => {
      const productCol: ColumnDef<RowData> = {
        id: `product-${index}`,
        header: "",
        size: 250,
        cell: (cell) => {
          const data = cell.row.original;
          const product = tableProducts[index];

          if (!product) {
            return null;
          }
          let label = "";
          let value = null;

          switch (data.index) {
            case 0:
              label = t("productDetailsTable.schaefflerId");
              value = product?.SchaefflerId || "-";
              break;

            case 1:
              label = t("productDetailsTable.customerMaterialId");
              value = product?.customerMaterialIdValue ? product.customerMaterailId : "-";
              break;

            case 2:
              label = t("productDetailsTable.coo");
              value = product?.countryOfOrigin || "-";
              break;

            case 3:
              label = t("comparisonCard.replaces");
              value = product?.replaces || "-";
              break;
          }

          if (isDesktop) {
            return value;
          }

          return (
            <Stack>
              {typeof value === "string" ? <Typography>{value}</Typography> : value}
              <Typography variant="bodySmall" color="textSecondary">
                {label}
              </Typography>
            </Stack>
          );
        },
      };
      return productCol;
    };

    if (isDesktop) {
      columnConfig.push(labelCol);
    }
    products.forEach((product, index) => {
      columnConfig.push(makeProductCol(index));
    });
    placeholders.forEach((product) => {
      columnConfig.push(makeProductCol(product));
    });

    if (isDesktopL) {
      const len = columnConfig.length;
      columnConfig[len - 1].size = undefined;
    }

    return columnConfig;
  }, [tableProducts, isDesktop, isDesktopL, t, products, placeholders]);

  if (!hasProductDetailsData) {
    return null;
  }

  if (data.length === 0) {
    return null;
  }

  return (
    <Accordion defaultExpanded>
      <AccordionSummary>
        <Typography>{t("productInfoTable.tableName")}</Typography>
      </AccordionSummary>
      <PCAccordionDetails>
        <PCTableContainer>
          <TanStackTable
            key={`${products.length}-${data.length}`}
            data={data}
            columns={columnConfig}
            variant={"productComparison"}
            state={{
              columnPinning: {
                left: ["label"],
              },
            }}
          />
        </PCTableContainer>
      </PCAccordionDetails>
    </Accordion>
  );
});

export default ProductDetailsTable;
